import styled from 'styled-components';
import { Colors } from 'basic-ui-lib/constants';
import { Text } from 'basic-ui-lib/components/Text';
import { IcFolder, IcCheck } from 'basic-ui-lib/icons';
import { IMediaItem as ILibraryItem, MediaType } from 'modules/media/interfaces/api';
import { RenderMedia } from 'basic-ui-lib/components/screen/RenderMedia';

interface MediaItemCardProps {
  item: ILibraryItem;
  selected?: boolean;
  onClick?: () => void;
}

const formatFileSize = (bytes: number | null): string => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes}B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)}KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
};

const getFileExtension = (name: string): string => {
  const parts = name.split('.');
  if (parts.length > 1) {
    return parts[parts.length - 1].toUpperCase();
  }
  return '';
};

const MediaItemCard = ({ item, selected, onClick }: MediaItemCardProps) => {
  const isFolder = item.type === MediaType.FOLDER;
  // const isVideo = item.type === MediaType.VIDEO;
  const fileExt = getFileExtension(item.name);
  const fileSize = formatFileSize(item.size);
  
  const childCount = Array.isArray(item.children) ? item.children.length : 0;

  if (isFolder) {
    return (
      <CardContainer onClick={onClick}>
        <ThumbnailWrapper $isFolder>
          <IcFolder width={64} height={64} />
        </ThumbnailWrapper>
        <ItemInfo>
          <Text body_1 semibold color="neutral.text.5" className="item-name">
            {item.name}
          </Text>
          <Text caption_3 color="neutral.text.4">
            • {childCount} item{childCount !== 1 ? 's' : ''}
          </Text>
        </ItemInfo>
      </CardContainer>
    );
  }

  return (
    <CardContainer onClick={onClick} $selected={selected}>
      <ThumbnailWrapper>
        {/* {isVideo ? (
          <VideoThumbnail src={item.url || ''} muted loop playsInline />
        ) : item.url ? (
          <Thumbnail src={item.url} alt={item.name} />
        ) : (
          <PlaceholderThumbnail />
        )}
        {isVideo && (
          <PlayOverlay>
            <IcPlay width={40} height={40} />
          </PlayOverlay>
        )} */}
        {RenderMedia(item, 0, false)}
        {selected && (
          <SelectedBadge>
            <IcCheck width={28} height={28} fill={Colors.secondary[5]} />
          </SelectedBadge>
        )}
      </ThumbnailWrapper>
      <ItemInfo>
        <Text body_1 semibold color="neutral.text.5" className="item-name">
          {item.name}
        </Text>
        <MetaInfo>
          {fileExt && (
            <Text caption_3 color="neutral.text.4">
              • {fileExt}
            </Text>
          )}
          {fileSize && (
            <Text caption_3 color="neutral.text.4">
              • {fileSize}
            </Text>
          )}
        </MetaInfo>
      </ItemInfo>
    </CardContainer>
  );
};

export default MediaItemCard;

const CardContainer = styled.div<{ $selected?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  padding: 8px;
  border-radius: 4px;
  border: 2px solid ${({ $selected }) => ($selected ? Colors.primary[5] : 'transparent')};
  transition: all 0.2s ease;
  max-width: 220px;
  width: 100%;

  &:hover {
    background: ${Colors.neutral.bg[2]};
  }
`;

const ThumbnailWrapper = styled.div<{ $isFolder?: boolean }>`
  width: 100%;
  height: 120px;
  border-radius: 4px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  background: ${({ $isFolder }) => ($isFolder ? 'transparent' : Colors.neutral.bg[2])};
`;

// const Thumbnail = styled.img`
//   width: 100%;
//   height: 100%;
//   object-fit: cover;
// `;

// const VideoThumbnail = styled.video`
//   width: 100%;
//   height: 100%;
//   object-fit: cover;
// `;

// const PlaceholderThumbnail = styled.div`
//   width: 100%;
//   height: 100%;
//   display: flex;
//   align-items: center;
//   justify-content: center;
//   background: ${Colors.neutral.bg[3]};
// `;

// const PlayOverlay = styled.div`
//   position: absolute;
//   top: 50%;
//   left: 50%;
//   transform: translate(-50%, -50%);
//   display: flex;
//   align-items: center;
//   justify-content: center;
// `;

const SelectedBadge = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
`;

const ItemInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  width: 100%;
  padding: 0 4px;

  .item-name {
    text-align: center;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 100%;
  }
`;

const MetaInfo = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 4px;
`;
